import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';

const { FiCheckCircle, FiLoader, FiAlertTriangle, FiArrowRight, FiHome } = FiIcons;

const PaymentSuccess = () => {
  const { session } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [requestDetails, setRequestDetails] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const stripeSessionId = searchParams.get('session_id');

    console.log('Payment Success Page Loaded');
    console.log('Session ID from URL:', stripeSessionId);
    console.log('Auth session:', session ? 'Present' : 'Missing');

    if (!stripeSessionId) {
      setStatus('error');
      setError('No payment session ID was found. Please contact support if you have been charged.');
      return;
    }

    const verifyPayment = async () => {
      try {
        const url = `${import.meta.env.VITE_STRIPE_VERIFY_PAYMENT}`;
        console.log('Making request to:', url);

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ session_id: stripeSessionId }),
        });

        console.log('Response status:', response.status);
        console.log('Response OK:', response.ok);

        // Try to get response text first
        const responseText = await response.text();
        console.log('Raw response text:', responseText);

        // Try to parse as JSON
        let errorData;
        try {
          errorData = JSON.parse(responseText);
          console.log('Parsed error data:', errorData);
        } catch (e) {
          console.error('Failed to parse response as JSON:', e);
          throw new Error(`Server returned invalid response (${response.status}): ${responseText.substring(0, 200)}`);
        }

        if (!response.ok) {
          const errorMsg = errorData.message || errorData.error || errorData.msg || 'Failed to verify payment.';
          console.error('Server error message:', errorMsg);
          throw new Error(errorMsg);
        }

        console.log('Success data:', errorData);
        setRequestDetails(errorData.requestDetails);
        setStatus('success');

      } catch (err) {
        console.error('Payment verification error:', err);
        setError(err.message || 'An unexpected error occurred');
        setStatus('error');
      }
    };

    verifyPayment();

  }, [searchParams, session]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center">
            <FiLoader className="animate-spin text-4xl text-primary-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800">Verifying Payment...</h2>
            <p className="text-gray-600 mt-2">Please wait a moment, we're confirming your transaction.</p>
          </div>
        );
      case 'error':
        return (
          <div className="text-center">
            <FiAlertTriangle className="text-5xl text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-800">Payment Verification Failed</h2>
            <p className="text-gray-600 mt-2 bg-red-50 p-4 rounded-md whitespace-pre-wrap">{error}</p>
            <div className="mt-4 text-xs text-gray-500 bg-gray-100 p-3 rounded">
              <p>Check the browser console for detailed error logs</p>
            </div>
            <Link
              to="/dashboard"
              className="mt-6 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              Go to Dashboard <FiArrowRight className="ml-2" />
            </Link>
          </div>
        );
      case 'success':
        return (
          <div className="text-center">
            <FiCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
            <h2 className="text-3xl font-extrabold text-gray-900">Thank You! Your Request is Confirmed.</h2>
            <p className="text-lg text-gray-600 mt-2">We've received your payment and your service request is now active.</p>

            
            
            <div className="mt-8">
                <h4 className="font-bold text-gray-800">What's Next?</h4>
                <p className="text-gray-600">Our team will review the details and contact you within 24 hours to coordinate the next steps. You can track the status of your request in your dashboard.</p>
            </div>

            <div className="mt-8 flex justify-center gap-4">
              <Link
                to="/dashboard"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
              >
                Go to Dashboard <FiArrowRight className="ml-2" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <FiHome className="mr-2" /> Back to Home
              </Link>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div 
        className="max-w-2xl w-full bg-white shadow-xl rounded-2xl p-8 md:p-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {renderContent()}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;